import {
  ComponentQuery,
  DOSourceDefinition,
  DOSourceVariable,
  Grant,
  LocationType,
  OutputNodeDefinition,
  Primitive,
  Provider,
  ResolveParamValuesParams,
  StoredProcedureScope,
  TokenAllowedApis,
  WorkflowAsTasks,
  WorkflowCodeType,
  WorkflowComponentOutputType,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  WorkflowComponentProblems,
  WorkflowComponentValue,
  WorkflowComponentValueType,
  WorkflowComponentValues,
  WorkflowDiagram,
  WorkflowEnvVars,
  WorkflowTableSchemas,
  WorkflowValidity,
  WorkflowVariable
} from '../types'

import { md5 } from 'js-md5'
import { dedent } from 'ts-dedent'
import { Toolbox } from '../components'
import { WorkflowComponent } from '../components/common/workflowcomponent'
import { ImportURL } from '../components/importurl'
import { ApiOutput } from '../components/apioutput'
import { McpToolOutput } from '../components/mcptooloutput'
import { ReadQuery } from '../components/readquery'
import { ReadTable } from '../components/readtable'
import { hashString, isExpressionValue, sortByName } from '../utils'
import { DataWarehouseConnection } from './connections'
import { DBricksConnection } from './connections/databricks'

const OUTPUT_COMPONENTS = {
  [StoredProcedureScope.Api]: new ApiOutput(),
  [StoredProcedureScope.McpTool]: new McpToolOutput()
} as const

class Workflow {
  id: string
  components: Record<string, WorkflowComponent>
  componentValues: Record<string, WorkflowComponentValues>
  variables: WorkflowVariable[]
  title: string
  diagram?: WorkflowDiagram
  doVariables: Record<string, DOSourceVariable[]>
  constructor(
    id: string,
    components: Record<string, WorkflowComponent>,
    componentValues: Record<string, WorkflowComponentValues>,
    variables: WorkflowVariable[],
    title: string,
    doVariables: Record<string, DOSourceVariable[]> = {}
  ) {
    this.id = id
    this.title = title
    this.components = components
    this.componentValues = componentValues
    this.variables = variables || []
    this.doVariables = doVariables
  }

  static fromDiagram(id: string, diagram: WorkflowDiagram, toolbox: Toolbox): Workflow {
    const components: Record<string, WorkflowComponent> = {}
    const componentValues: Record<string, WorkflowComponentValues> = {}
    const doVariables: Record<string, DOSourceVariable[]> = {}
    for (const node of diagram.nodes) {
      if (node.type && ['source', 'generic', 'SourceDO'].includes(node.type)) {
        const component = toolbox.components[node.data.name]
        if (!component) {
          continue
        }
        if (!(node.id in componentValues)) {
          componentValues[node.id] = {}
        }
        for (const input of node.data.inputs) {
          componentValues[node.id][input.name] = {
            type: WorkflowComponentValueType.Literal,
            value: input.value as Primitive
          }
        }
        for (const input of component.inputs) {
          if (input.type === WorkflowComponentParamType.InputTable) {
            componentValues[node.id][input.name] = []
          }
        }
        if (node.type === 'SourceDO' && node.data.variables) {
          doVariables[node.id] = node.data.variables
        }
        components[node.id] = component
      }
    }
    for (const edge of diagram.edges) {
      const inputName = edge.targetHandle as string
      const outputName = edge.sourceHandle as string
      const parentComponent = components[edge.source]
      if (
        !componentValues[edge.target] ||
        !componentValues[edge.target][inputName] ||
        !parentComponent ||
        !parentComponent.outputs.map((output) => output.name).includes(outputName)
      ) {
        continue
      }
      if (componentValues[edge.target][inputName]) {
        ;(componentValues[edge.target][inputName] as WorkflowComponentValue[]).push({
          type: WorkflowComponentValueType.Node,
          value: {
            component: edge.source,
            output: edge.sourceHandle
          }
        })
      }
    }
    for (const [componentName, component] of Object.entries(components)) {
      for (const input of component.inputs) {
        if (!componentValues[componentName] || !componentValues[componentName][input.name]) {
          continue
        }
        if (
          input.type === WorkflowComponentParamType.InputTable &&
          !(input.mode === WorkflowComponentParameterMode.Multiple)
        ) {
          const paramValue = componentValues[componentName][input.name] as WorkflowComponentValue[]
          if (paramValue.length && paramValue[0].value) {
            componentValues[componentName][input.name] = paramValue[0]
          } else {
            componentValues[componentName][input.name] = {
              type: WorkflowComponentValueType.Literal,
              value: null
            }
          }
        }
      }
    }
    const workflow = new Workflow(id, components, componentValues, diagram.variables ?? [], diagram.title, doVariables)
    workflow.diagram = diagram
    return workflow
  }

  static fromExportedStoredProcedure(procedure: string, toolbox: Toolbox) {
    const metadataComment = procedure.match(/\/\*([\S\s]*?diagramJson[\S\s]*?)\*\//)
    let procMetadata: Record<string, unknown> = {}
    if (metadataComment) {
      try {
        procMetadata = JSON.parse(metadataComment[1])
      } catch (e) {
        throw new Error('Error parsing procedure metadata')
      }
    } else {
      throw new Error('Missing procedure metadata')
    }
    let diagram = null
    try {
      diagram = JSON.parse(procMetadata.diagramJson as string)
    } catch (e) {
      throw new Error('Error parsing workflow diagram')
    }
    if (diagram) {
      const workflow = Workflow.fromDiagram(hashString(procedure), diagram, toolbox)
      return workflow
    } else {
      throw new Error('Null workflow diagram')
    }
  }

  addComponent(componentClass: string, componentName: string, toolbox: Toolbox) {
    const component = toolbox.components[componentClass]
    if (!component) {
      throw new Error(`Unknown component: ${componentClass}`)
    }
    this.components[componentName] = component
    this.componentValues[componentName] = {}
  }

  addLiteralComponentInputValue(componentName: string, inputName: string, value: Primitive | Array<Primitive>) {
    if (!(componentName in this.componentValues)) {
      throw new Error(`Wrong component name: ${componentName}`)
    }
    const inputNames = this.components[componentName].inputs.map((input) => input.name)
    if (!inputNames.includes(inputName)) {
      throw new Error(`Wrong input name: ${inputName}`)
    }
    this.componentValues[componentName][inputName] = {
      type: WorkflowComponentValueType.Literal,
      value: value
    }
  }

  addNodeComponentInputValue(componentName: string, inputName: string, sourceComponent: string, sourceOutput: string) {
    if (!(componentName in this.componentValues)) {
      throw new Error(`Wrong component name: ${componentName}`)
    }
    const inputNames = this.components[componentName].inputs.map((input) => input.name)
    if (!inputNames.includes(inputName)) {
      throw new Error(`Wrong input name: ${inputName}`)
    }
    this.componentValues[componentName][inputName] = {
      type: WorkflowComponentValueType.Node,
      value: { component: sourceComponent, output: sourceOutput }
    }
  }

  setComponentDoVariables(componentName: string, doVariables: DOSourceVariable[]) {
    if (!(componentName in this.componentValues)) {
      throw new Error(`Wrong component name: ${componentName}`)
    }
    this.doVariables[componentName] = doVariables
  }

  getComponentDependencies(componentName: string, recurse = true): Array<string> {
    if (!(componentName in this.componentValues)) {
      throw new Error(`Workflow does not contain component ${componentName}`)
    }
    const allDependencies: Array<string> = []
    for (let value of Object.values(this.componentValues[componentName])) {
      if (!Array.isArray(value)) {
        value = [value]
      }
      for (const subvalue of value) {
        if (subvalue.type === WorkflowComponentValueType.Node) {
          const parent = (subvalue.value as OutputNodeDefinition).component
          allDependencies.push(parent)
          if (recurse) {
            const dependencies = this.getComponentDependencies(parent)
            allDependencies.push(...dependencies)
          }
        }
      }
    }
    return allDependencies
  }

  _getAdjacencyArray(onlyDownStream = false): Record<string, Array<string>> {
    const adjacency: Record<string, Array<string>> = {}
    for (const componentName of Object.keys(this.components)) {
      for (let value of Object.values(this.componentValues[componentName])) {
        value = Array.isArray(value) ? value : [value]
        for (const subvalue of value) {
          if (subvalue.type === WorkflowComponentValueType.Node) {
            const parent = (subvalue.value as OutputNodeDefinition).component
            if (!(parent in adjacency)) {
              adjacency[parent] = []
            }
            if (!(componentName in adjacency)) {
              adjacency[componentName] = []
            }
            adjacency[parent].push(componentName)
            if (!onlyDownStream) {
              adjacency[componentName].push(parent)
            }
          }
        }
      }
    }
    return adjacency
  }

  _getSubgraphs(): Array<Array<string>> {
    const subgraphs: Array<Array<string>> = []
    const adjacency = this._getAdjacencyArray()
    function _addToSubgraph(subgraph: Array<string>, componentName: string) {
      if (!subgraph.includes(componentName)) {
        subgraph.push(componentName)
        for (const adjacent of adjacency[componentName] ?? []) {
          _addToSubgraph(subgraph, adjacent)
        }
      }
    }
    for (const componentName of Object.keys(this.components)) {
      const usedComponents: Array<string> = Array.prototype.concat.apply([], Object.values(subgraphs))
      if (!usedComponents.includes(componentName)) {
        const subgraph: Array<string> = []
        _addToSubgraph(subgraph, componentName)
        subgraphs.push(subgraph)
      }
    }
    subgraphs.sort(function (a, b) {
      return b.length - a.length
    })
    return subgraphs
  }

  getDependentComponentsInRunningOrder(componentName: string): Array<string> {
    const components = this.getComponentsInRunningOrder()
    const subgraphs = this._getSubgraphs()
    const componentSubgraph = subgraphs.find((subgraph) => {
      return subgraph.includes(componentName)
    })
    if (!componentSubgraph) {
      throw new Error(`Component ${componentName} not found in any subgraph`)
    }
    return components.filter((c) => {
      return componentSubgraph.includes(c)
    })
  }

  getComponentsInRunningOrder(): Array<string> {
    const componentNames: Array<string> = []
    const subgraphs = this._getSubgraphs()
    for (const subgraph of subgraphs) {
      const subgraphComponentNames: Array<string> = []
      const workflowParams = this.componentValues
      const hasPendingDependencies = function (componentName: string) {
        if (!workflowParams[componentName]) {
          return false
        }
        for (let value of Object.values(workflowParams[componentName])) {
          value = Array.isArray(value) ? value : [value]
          for (const subvalue of value) {
            if (
              subvalue.type === WorkflowComponentValueType.Node &&
              !subgraphComponentNames.includes((subvalue.value as OutputNodeDefinition).component)
            ) {
              return true
            }
          }
        }
        return false
      }
      while (subgraphComponentNames.length !== subgraph.length) {
        let found = false
        for (const componentName of subgraph) {
          if (!subgraphComponentNames.includes(componentName)) {
            if (!hasPendingDependencies(componentName)) {
              subgraphComponentNames.push(componentName)
              found = true
            }
          }
        }
        if (!found) {
          throw new Error(
            'Cannot resolve running order. There might be unresolved or circular dependencies in the workflow'
          )
        }
      }
      componentNames.push(...subgraphComponentNames)
    }

    return componentNames
  }

  /**
   * Returns the dependency between source tables and tables generated by the workflow.
   * This information can be used to determine which tables need to be updated if a source
   * table has changed, and which ones are be affected by this change in the source table.
   * @param connection The connection to the data warehouse
   * @param envVars The environment variables to use when running the workflow
   * @param codeType The type of code to use when running the workflow
   * @returns A dictionary with source tables as keys and a list of dependent tables as values
   */
  getSourcesAndDependentOutputs(
    connection: DataWarehouseConnection,
    envVars: WorkflowEnvVars,
    codeType: WorkflowCodeType
  ): Record<string, Array<string>> {
    const downstreamOutputs: Record<string, Array<string>> = {}
    const adjacency = this._getAdjacencyArray(true)
    const outputs = this.getOutputTables(connection, codeType)
    function _addToDownstreamOutputs(dependentOutputs: Array<string>, componentName: string) {
      if (outputs[componentName]) {
        dependentOutputs.push(
          ...(Object.values(outputs[componentName]) as Array<string>).map((x) => x.split('.').pop() as string)
        )
      }
      for (const adjacent of adjacency[componentName] ?? []) {
        _addToDownstreamOutputs(dependentOutputs, adjacent)
      }
    }
    for (const [componentName, component] of Object.entries(this.components)) {
      if (component.metadata.name === new ReadTable().metadata.name) {
        const componentDependentOutputs = []
        for (const child of adjacency[componentName] ?? []) {
          _addToDownstreamOutputs(componentDependentOutputs, child)
        }
        downstreamOutputs[
          (Object.values(this.componentValues[componentName])[0] as WorkflowComponentValue).value as unknown as string
        ] = componentDependentOutputs
      }
    }
    for (const k of Object.keys(downstreamOutputs)) {
      downstreamOutputs[k] = [...new Set(downstreamOutputs[k])]
    }
    return downstreamOutputs
  }

  workflowMetadataAsCommentString(codeType: WorkflowCodeType): string {
    const type = codeType === WorkflowCodeType.DryRun ? 'DRY-RUN' : 'RUN'
    return dedent`
      Code generated by Carto Workflows Engine (${type})
      - workflowid:${this.id}
      `
  }

  /**
   * Returns the code corresponding to this workflow
   * @param connection The connection where the workflow is to be run
   * @param envVars The environment variables for running this workflow
   * @param codeType The type of code to return
   * @param schemas The schemas of the tables in the workflow
   * @returns The SQL code corresponding to this workflow when run as a component of a larger workflow
   */
  async getWorkflowCode(
    connection: DataWarehouseConnection,
    envVars: WorkflowEnvVars,
    codeType: WorkflowCodeType,
    schemas?: WorkflowTableSchemas
  ): Promise<string> {
    let preQuery = ''
    if (codeType === WorkflowCodeType.FullNoCache) {
      // Drop output tables when not using cache mode.
      // This is used to ensure output tables are created
      // in the same execution to update each node status.
      preQuery = this.getDropTablesCode(connection)
    }
    return dedent(await connection.getWorkflowCode(this, envVars, codeType, schemas, preQuery)).replace(
      /(\r\n|\n|\r){2,}/g,
      '\n\n'
    )
  }

  /**
   * Returns the SQL corresponding to this workflow wrapped in a stored procedure
   *
   * @param connection The connection where the workflow is to be run
   * @param envVars: workflow environment variables
   * @param isImmutable: whether the workflow is inmutable or not.
   * If so, the cached result table will be used if exists
   * @param includeDiagram: whether to include a diagram of the workflow as a comment
   * @param scope: the usage of the stored procedure
   * @param schemas (optional): workflow schemas
   */
  async getAsStoredProcedure(
    connection: DataWarehouseConnection,
    envVars: WorkflowEnvVars,
    isImmutable = true,
    includeDiagram = true,
    scope: StoredProcedureScope,
    schemas?: WorkflowTableSchemas
  ): Promise<string> {
    let code = await connection.getAsStoredProcedure(this, envVars, isImmutable, includeDiagram, scope, schemas)
    if (this.requiredAnalyticsToolboxVersion(connection.type) !== null) {
      code = dedent`
          -- WARNING: This procedure requires the Analytics Toolbox and assumes it will be located
          -- at the following path: ${envVars.analyticsToolboxDataset}. If you want to deploy and
          -- run it in a different location, you will need to update the code accordingly.
          ${code}`
    }
    return dedent(code.replace(/\n\s*\n/g, '\n'))
  }

  /**
   * Returns a list of tasks defining the workflow. This can be used to recreate the workflow
   * in the data warehouse, using its native implementation of workflows
   * @param connection the connection where the workflow is to be run
   * @param envVars the environment variables for running this workflow
   * @param scope the usage of the stored procedure
   * @param schemas the schemas of the tables in the workflow
   * @returns A list of tasks defining the workflow
   */
  async getAsTasksList(
    connection: DataWarehouseConnection,
    envVars: WorkflowEnvVars,
    isImmutable = true,
    scope: StoredProcedureScope,
    schemas?: WorkflowTableSchemas
  ): Promise<WorkflowAsTasks | null> {
    return connection.getAsTasksList(this, envVars, isImmutable, scope, schemas)
  }

  /**
   * Returns the literal parameters required to compose the code of the workflow, from the components
   * it contains and the link between them.
   * @returns The parameters to use when writing the code of the workflow
   */
  resolveParameterValues({ connection, codeType, workflowParams, schemas }: ResolveParamValuesParams) {
    const outputValues: Record<string, Record<string, unknown>> = {}
    workflowParams ||= this.componentValues
    const orderedComponents = this.getComponentsInRunningOrder()

    const componentParams: Record<string, Record<string, unknown>> = {}
    const isExecutable: Record<string, unknown> = {}
    for (const componentName of orderedComponents) {
      try {
        const component = this.components[componentName]
        if (!component) {
          continue
        }
        componentParams[componentName] = {}

        for (const [objectKey, objectValue] of Object.entries(workflowParams[componentName])) {
          let value = objectValue
          const isArray = Array.isArray(value)
          if (!isArray) {
            value = [value as WorkflowComponentValue]
          }
          const valueArray: Array<unknown> = []
          for (const subvalue of value as WorkflowComponentValue[]) {
            if (subvalue.type === WorkflowComponentValueType.Node) {
              const node = subvalue.value as OutputNodeDefinition
              if (node.output) {
                if (isExecutable[node.component]) {
                  valueArray.push(outputValues[node.component]?.[node.output])
                }
              }
            } else {
              if (isExpressionValue(subvalue)) {
                const expression = `${subvalue.value}`.replace(/\{\{/g, '(').replace(/\}\}/g, ')')
                valueArray.push(expression)
              } else {
                valueArray.push(subvalue.value)
              }
            }
          }
          if (isArray) {
            componentParams[componentName][objectKey] = valueArray
          } else {
            componentParams[componentName][objectKey] = valueArray[0]
          }
        }

        try {
          const outputTables = component.getOutputTables({
            workflow: this,
            params: componentParams[componentName],
            connection,
            codeType,
            componentName
          })
          if (outputTables) {
            outputValues[componentName] = {}
            for (const [outputName, outputValue] of Object.entries(outputTables)) {
              outputValues[componentName][outputName] = outputValue
            }
          }
        } catch (e) {
          outputValues[componentName] = {}
          for (const output of component.outputs) {
            outputValues[componentName][output.name] = null
          }
        }
        try {
          isExecutable[componentName] =
            component.checkInputValues({
              params: this.componentValues[componentName],
              resolvedParamValues: componentParams[componentName],
              connection: connection,
              dryRun: codeType === WorkflowCodeType.DryRun,
              variables: this.variables,
              schemas
            }).length === 0
        } catch (e) {
          isExecutable[componentName] = false
        }
      } catch (e) {
        componentParams[componentName] = Object.keys(workflowParams[componentName]).reduce((acc, prop) => {
          acc[prop] = null
          return acc
        }, {})
      }
    }
    return componentParams
  }

  /**
   * Returns the queries corresponding to the components of the workflow
   * @param connection The connection where the workflow will be executed
   * @param envVars The environment variables for running this workflow
   * @param codeType The type of code to return
   * @param schemas The schemas of the tables in the workflow
   * @returns The queries corresponding to the components of the workflow
   */
  async getSubqueries(
    connection: DataWarehouseConnection,
    envVars: WorkflowEnvVars,
    codeType: WorkflowCodeType,
    schemas?: WorkflowTableSchemas
  ): Promise<Array<ComponentQuery>> {
    const orderedComponents = this.getComponentsInRunningOrder()
    const componentParams = this.resolveParameterValues({
      connection,
      codeType,
      schemas
    })
    const queries: Array<ComponentQuery> = []

    for (const componentName of orderedComponents) {
      const component = this.components[componentName]
      if (!component) {
        continue
      }
      let inOutSchemas
      let codeReturn
      const errors = component.checkInputValues({
        params: this.componentValues[componentName],
        resolvedParamValues: componentParams[componentName],
        connection: connection,
        dryRun: codeType === WorkflowCodeType.DryRun,
        schemas,
        variables: this.variables
      })
      if (errors.length) {
        codeReturn = {
          query: '',
          outputs: {}
        }
      } else {
        if (schemas?.length) {
          // table schemas use the FQNs of the dry runtables, so we need them
          // for linking a given input or output to its expected schema
          const dryRunComponentParams = this.resolveParameterValues({
            connection,
            codeType: WorkflowCodeType.DryRun,
            schemas
          })
          const inputSchemas = {}
          for (const input of component.inputs) {
            if (input.type === WorkflowComponentParamType.InputTable) {
              const inputName = input.name
              const inputTable = dryRunComponentParams[componentName][inputName] as string | string[]
              for (const componentSchemas of schemas) {
                for (const schema of componentSchemas.schemas) {
                  if (inputTable?.includes?.(schema.fqn)) {
                    if (Array.isArray(inputTable)) {
                      if (inputSchemas[inputName]) {
                        inputSchemas[inputName].push(schema.schema)
                      } else {
                        inputSchemas[inputName] = [schema.schema]
                      }
                    } else {
                      inputSchemas[inputName] = schema.schema
                    }
                  }
                }
              }
            }
          }
          const outputSchemas = {}
          const componentSchemas = schemas.find((s) => s.componentName === componentName)?.schemas
          for (const output of component.outputs) {
            if (output.type === WorkflowComponentOutputType.Table) {
              outputSchemas[output.name] = componentSchemas?.find((s) => s.name === output.name)?.schema
            }
          }
          inOutSchemas = {
            input: inputSchemas,
            output: outputSchemas
          }
        }
        codeReturn = await component.getFullCodeSnippet({
          workflow: this,
          params: componentParams[componentName],
          connection,
          envVars,
          componentName,
          codeType,
          schemas: inOutSchemas
        })
      }
      queries.push({ componentName, ...codeReturn })
    }

    return queries
  }

  /**
   * Returns the output tables generated by the workflow
   * @param connection The connection where the workflow will be executed
   * @param codeType The type of code to return
   * @param schemas The schemas of the tables in the workflow
   * @returns The output tables generated by the workflow
   */
  getOutputTables(connection: DataWarehouseConnection, codeType: WorkflowCodeType, schemas?: WorkflowTableSchemas) {
    const orderedComponents = this.getComponentsInRunningOrder()
    const componentParams = this.resolveParameterValues({
      connection,
      codeType,
      schemas
    })
    const tables: Record<string, Record<string, string>> = {}
    for (const componentName of orderedComponents) {
      const component = this.components[componentName]
      if (!component) {
        continue
      }
      const errors = component.checkInputValues({
        params: this.componentValues[componentName],
        resolvedParamValues: componentParams[componentName],
        connection: connection,
        dryRun: codeType === WorkflowCodeType.DryRun,
        schemas,
        variables: this.variables
      })
      if (!errors.length) {
        tables[componentName] = component.getOutputTables({
          workflow: this,
          params: componentParams[componentName],
          connection,
          componentName,
          codeType
        })
      }
    }
    return tables
  }

  /**
   * Returns the SQL query to get a list of tables applying a filter.
   * @param connection The connection where the workflow will be executed
   * @param filter The filter to apply to the list of tables
   * @param locationType The working location type to use when getting the list of tables
   * @returns Returns the SQL query to get a list of tables applying a filter.
   */
  getTableListQuery(
    connection: DataWarehouseConnection,
    filter: string,
    locationType: LocationType = LocationType.Temp
  ): string | null {
    return connection.getTableListQuery(filter, locationType)
  }

  /**
   * Returns true if the workflow has a table defined as the output of the
   * corresponding stored procedure.
   * This table is defined by linking it to a Output component.
   * @param scope The usage of the stored procedure
   * @returns true if the workflow has a table defined as the output of the
   * corresponding stored procedure.
   */
  hasStoredProcedureOutputTable(scope: StoredProcedureScope): boolean {
    const outputComponent = OUTPUT_COMPONENTS[scope]
    if (outputComponent) {
      return Object.values(this.components).some(
        (component) => component.metadata.name === outputComponent.metadata.name
      )
    }
    return false
  }

  /**
   * Returns the name of the table to be used as the output for the workflow stored
   * procedure. The name is a fully qualified name.
   * @param connection The connection where the workflow is to be run
   * @param scope The usage of the stored procedure
   * @returns The name of the table defined in the workflow as the output to be used
   * for a stored procedure.
   */
  getStoredProcedureOutputTableFullName(connection: DataWarehouseConnection, scope: StoredProcedureScope): string {
    return connection.getStoredProcedureOutputTableFullNameForProcedureName(this.getStoredProcedureName(scope))
  }

  /**
   * Returns the name of the table used as source to produce the table
   * that will be the output of the workflow stored procedure
   * @param connection The connection where the workflow will be executed
   * @param scope The usage of the stored procedure
   * @returns The name of the table used as source to produce the table
   * that will be the output of the workflow stored procedure
   */
  getStoredProcedureOutputSourceTableName(
    connection: DataWarehouseConnection,
    scope: StoredProcedureScope
  ): string | undefined {
    const outputComponent = OUTPUT_COMPONENTS[scope]
    if (!outputComponent) {
      return undefined
    }
    const orderedComponents = this.getComponentsInRunningOrder()
    const componentParams = this.resolveParameterValues({
      connection,
      codeType: WorkflowCodeType.StoredProcedure
    })
    for (const componentName of orderedComponents) {
      const component = this.components[componentName]
      if (component?.metadata.name === outputComponent.metadata.name) {
        return componentParams[componentName].source as string
      }
    }
  }

  /**
   * Returns true if the output table of the workflow stored procedure
   * will return the full content of the table.
   * @param connection The connection where the workflow will be executed
   * @param scope The usage of the stored procedure
   * @returns True if the output table of the workflow stored procedure
   * will return the full content of the table.
   */
  getStoredProcedureOutputIsTableFullContent(
    connection: DataWarehouseConnection,
    scope: StoredProcedureScope
  ): boolean | undefined {
    const outputComponent = OUTPUT_COMPONENTS[scope]
    if (!outputComponent) {
      return undefined
    }
    const orderedComponents = this.getComponentsInRunningOrder()
    const componentParams = this.resolveParameterValues({
      connection,
      codeType: WorkflowCodeType.StoredProcedure
    })
    for (const componentName of orderedComponents) {
      const component = this.components[componentName]
      if (component?.metadata.name === outputComponent.metadata.name) {
        return componentParams[componentName].mode === 'sync'
      }
    }
  }

  getParamsHash(values: Record<string, unknown>) {
    const publicVars = this.variables.filter((v) => v.scopes?.length > 0).sort(sortByName)
    return md5(publicVars.map((v) => values[v.name]).join())?.slice(0, 16)
  }

  /**
   * Checks the validity of a workflow. It checks both the structure of the workflow and the validity
   * of the input values used as inputs for its components
   * @param connection The connection where the workflow will be executed
   * @param schemas The schemas of the tables used in the workflow
   * @returns A {WorkflowValidity} object with the problems encountered in the workflow
   */
  checkValidity(connection: DataWarehouseConnection, schemas?: WorkflowTableSchemas): WorkflowValidity {
    let orderedComponents: Array<string>
    try {
      orderedComponents = this.getComponentsInRunningOrder()
    } catch (e) {
      return {
        valid: false,
        problems: [{ message: `${e}` }]
      }
    }

    const componentParams = this.resolveParameterValues({
      connection,
      codeType: WorkflowCodeType.Full,
      schemas
    })
    const allProblems: Array<WorkflowComponentProblems> = []
    for (const componentName of orderedComponents) {
      const component = this.components[componentName]
      if (!component) {
        continue
      }
      const problems = component.checkInputValues({
        params: this.componentValues[componentName],
        resolvedParamValues: componentParams[componentName],
        connection: connection,
        dryRun: false,
        schemas,
        variables: this.variables
      })
      if (problems.length) {
        allProblems.push({
          componentName: componentName,
          problems: problems
        })
      }
    }

    if (allProblems.length) {
      return {
        valid: false,
        problems: allProblems
      }
    } else {
      return {
        valid: true
      }
    }
  }

  /**
   * Given the response obtained from the datawarehouse after running this workflow,
   * and assuming the execution has failed, tries to figure out the component that
   * is responsible for the failure
   * @param envVars The environment variables for running this workflow
   * @param connection The connection where the workflow is to be run
   * @param codeType The type of code to use when running the workflow
   * @param response The response as returned from the datawarehouse API
   * @returns
   */
  async getComponentFromApiResponse(
    envVars: WorkflowEnvVars,
    connection: DataWarehouseConnection,
    codeType: WorkflowCodeType,
    response: unknown
  ): Promise<string | null> {
    const lineNumber = connection.extractErrorLineNumberFromApiResponse(response)
    if (lineNumber) {
      return this.getComponentFromLineNumber(envVars, connection, codeType, lineNumber)
    } else {
      return null
    }
  }

  async getComponentFromLineNumber(
    envVars: WorkflowEnvVars,
    connection: DataWarehouseConnection,
    codeType: WorkflowCodeType,
    lineNumber: number
  ) {
    const query = await this.getWorkflowCode(connection, envVars, codeType)
    const lines = query.split('\n')
    let lastNode: string | null = null
    for (let index = 0; index < lineNumber; index++) {
      const line = lines[index]
      const match = line?.match(/==.*?\[(.*?)\].*?==/)
      if (match) {
        lastNode = match[1]
      }
    }
    return lastNode
  }

  /**
   * Returns the query to execute in the data warehouse to remove the stored procedure corresponding to this
   * workflow from the dataset where temporary tables are created for the passed connection
   * @param connection The connection where the workflow is to be run
   * @param name The full name of the stored procedure to remove
   * @returns The queries to execute in the data warehouse to remove the stored procedure corresponding to
   * this workflow from the dataset where temporary tables are created for the passed connection.
   */
  getRemoveStoredProcedureQueries(connection: DataWarehouseConnection, name: string): Array<string> {
    return connection.getRemoveStoredProcedureQueries(name)
  }

  getStoredProcedureName(scope: StoredProcedureScope): string {
    return `wfproc_${(scope || StoredProcedureScope.Api).toLowerCase()}_${hashString(this.id)}`
  }

  getRequiredGrants(connection: DataWarehouseConnection) {
    try {
      const componentParams = this.resolveParameterValues({
        connection,
        codeType: WorkflowCodeType.Full
      })
      const apis: Set<TokenAllowedApis> = new Set()
      const grants: Array<Grant> = []
      for (const [componentName, component] of Object.entries(this.components)) {
        const grantsAndApis = component.getRequiredGrants({
          workflow: this,
          connection,
          params: componentParams[componentName],
          componentName
        })
        grantsAndApis.apis.forEach(apis.add, apis)
        grants.push(...grantsAndApis.grants)
      }
      return {
        grants,
        referers: [],
        allowed_apis: Array.from(apis)
      }
    } catch (e) {
      return null
    }
  }

  /**
   * Returns true if table generated by the specified component in the specified output is used by any other component
   * in the workflow.
   * @param componentName The name of the component
   * @param outputName The name of the output
   */
  isTableUsed(componentName: string, outputName: string): boolean {
    const dependant = this.componentsUsingTable(componentName, outputName)
    return dependant.length > 0
  }

  /**
   * Returns the names of the components that use the table generated by the specified component in the specified output.
   * @param componentName The name of the component
   * @param outputName The name of the output
   * @param recursive Whether to recursively search for components that use the table generated by the specified component in the
   * specified output. If false, only the immediate dependencies are returned.
   * @returns An array with the names of the components that use the table generated by the specified component in the
   * specified output.
   * @throws An error if the component does not exist.
   */
  componentsUsingTable(componentName: string, outputName: string, recursive = false): Array<string> {
    const allDependencies: Array<string> = []
    if (!(componentName in this.componentValues)) {
      return allDependencies
    }
    for (const childComponentName of Object.keys(this.components)) {
      if (this.componentValues[childComponentName]) {
        for (let value of Object.values(this.componentValues[childComponentName])) {
          if (!Array.isArray(value)) {
            value = [value]
          }
          for (const subvalue of value) {
            if (subvalue.type === WorkflowComponentValueType.Node) {
              const parent = subvalue.value as OutputNodeDefinition
              if (parent.component === componentName && parent.output === outputName) {
                if (!allDependencies.includes(childComponentName)) {
                  allDependencies.push(childComponentName)
                }
                if (recursive) {
                  const childComponent = this.components[childComponentName]
                  childComponent.outputs.forEach((output) => {
                    const childDependencies = this.componentsUsingTable(childComponentName, output.name, recursive)
                    for (const childDependency of childDependencies) {
                      if (!allDependencies.includes(childDependency)) {
                        allDependencies.push(childDependency)
                      }
                    }
                  })
                }
              }
            }
          }
        }
      }
    }
    return allDependencies
  }

  componentsInmediatelyUpstream(componentName: string): Array<string> {
    const upstream: Array<string> = []
    if (!(componentName in this.componentValues)) {
      return upstream
    }
    for (let value of Object.values(this.componentValues[componentName])) {
      if (!Array.isArray(value)) {
        value = [value]
      }
      for (const subvalue of value) {
        if (subvalue.type === WorkflowComponentValueType.Node) {
          const parent = subvalue.value as OutputNodeDefinition
          if (!upstream.includes(parent.component)) {
            upstream.push(parent.component)
          }
        }
      }
    }
    return upstream
  }

  componentsInmediatelyDownstream(componentName: string): Array<string> {
    let downstream: Array<string> = []
    if (!(componentName in this.componentValues)) {
      return downstream
    }
    const component = this.components[componentName]
    for (const output of component.outputs) {
      const dependant = this.componentsUsingTable(componentName, output.name)
      downstream = downstream.concat(dependant.filter((item) => downstream.indexOf(item) < 0))
    }
    return downstream
  }

  /**
   * Returns the code to drop all tables generated by the workflow
   * @param connection The connection where the workflow is to be run
   * @returns The code to drop all tables generated by the workflow
   */
  getDropTablesCode(connection: DataWarehouseConnection): string {
    let tablesToDrop: {
      name: string
      type: WorkflowComponentOutputType | undefined
    }[] = []
    const outputTables = this.getOutputTables(connection, WorkflowCodeType.Full)

    for (const [componentName, tables] of Object.entries(outputTables)) {
      for (const [outputName, tableName] of Object.entries(tables)) {
        const output = this.components[componentName].outputs.find((x) => x.name === outputName)
        const metadataName = this.components[componentName].metadata.name
        if (metadataName !== new ImportURL().metadata.name) {
          // Skip ImportURL component
          tablesToDrop.push({
            name: tableName,
            type: output?.type
          })
        }
      }
    }
    tablesToDrop = tablesToDrop.filter((table) =>
      connection
        .unquoteName(connection.applyCapitalization(table.name))
        .startsWith(connection.unquoteName(connection.applyCapitalization(`${connection.tempLocation}.WORKFLOW_`)))
    )
    const uniqueTablesToDrop = [...new Set(tablesToDrop)]

    if (uniqueTablesToDrop.length === 0) {
      return ''
    }

    return uniqueTablesToDrop
      .map((table) => {
        const isView = table.type === WorkflowComponentOutputType.View
        const tableName = connection.quoteName(table.name)
        const dropQuery = `DROP ${isView ? 'VIEW' : 'TABLE'} IF EXISTS ${tableName};`
        if (connection.type === Provider.DatabricksRest) {
          return `${(connection as DBricksConnection).wrapSQLCodeAsPythonCall(dropQuery)}\n`
        }
        return `${dropQuery}\n`
      })
      .join('')
  }

  isLeafNode(componentName: string) {
    return this.componentsInmediatelyDownstream(componentName).length === 0
  }

  /**
   * Returns true if the specified parameter has a value assigned that is an expression
   * @param componentName The name of the component
   * @param parameterName The name of the parameter
   * @returns true if the specified parameter has a value assigned that is an expression
   */
  isExpression(componentName: string, parameterName: string) {
    if (!this.components[componentName]) {
      throw new Error(`Component '${componentName}' not found in workflow`)
    }
    const parameterValue = this.componentValues[componentName][parameterName]
    if (parameterValue) {
      return isExpressionValue(parameterValue as WorkflowComponentValue)
    } else {
      return false
    }
  }

  // if tablename is the output of a ReadQuery component with a DO source subscription
  // then this returns the subscription query and variables; otherwise it returns null
  tableDOSource(tablename: string, connection: DataWarehouseConnection): DOSourceDefinition | null {
    const componentParams = this.resolveParameterValues({
      connection,
      codeType: WorkflowCodeType.Full
    })
    for (const componentName in this.components) {
      const component = this.components[componentName]
      if (component.metadata.name == ReadQuery.NAME) {
        const preparedParams = component._prepareParameters(componentParams[componentName])
        const tags = (preparedParams.tags as string).split(',').map((x) => x.toLowerCase())
        if (tags.includes('do')) {
          const outputTables = [WorkflowCodeType.DryRun, WorkflowCodeType.Full].map((codeType) => {
            return component.getOutputTables({
              workflow: this,
              params: preparedParams,
              componentName,
              connection,
              codeType
            })?.['out']
          })
          if (outputTables.includes(tablename)) {
            const variables = this.doVariables[componentName] || null
            return {
              query: preparedParams.source as string,
              variables
            }
          }
        }
      }
    }
    return null
  }

  /**
   * Return the version of the analytics toolbox required to run this workflow
   * @param provider The provider where the component is to be run
   * @returns The version of the analytics toolbox required to run this workflow
   * Null if the component does not require the Analytics Toolbox
   */
  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    let requiredVersion: string | null = null
    for (const componentName in this.components) {
      const component = this.components[componentName]
      const version = component.requiredAnalyticsToolboxVersion(provider)
      if (version && (requiredVersion === null || version > requiredVersion)) {
        requiredVersion = version
      }
    }
    return requiredVersion
  }

  /**
   * Returns the name of the component that generates the specified output table
   * @param fqn The fully qualified name of the output table
   * @param connection The connection to the data warehouse
   * @param codeType The type of code to use when running the workflow
   * @param schemas The schemas of the tables in the workflow
   * @returns The name of the component that generates the specified output table
   * Null if the table is not generated by any component in the workflow
   */
  getComponentNameByOutputTable(
    fqn: string,
    connection: DataWarehouseConnection,
    codeType: WorkflowCodeType,
    schemas?: WorkflowTableSchemas
  ) {
    const outputTables = this.getOutputTables(connection, codeType, schemas)
    for (const [componentName, tables] of Object.entries(outputTables)) {
      for (const [, table] of Object.entries(tables)) {
        if (table === fqn) {
          return componentName
        }
      }
    }
  }

  /**
   * Returns a query that can be executed in the data warehouse to get the row counts of the tables generated
   * by a given component in the workflow.
   * @param connection The connection to the data warehouse
   * @param componentName The name of the component whose table counts are to be obtained.
   * @param params The parameters used to run the component
   * Returns null in case the provider does not require to run an extra query to get the row counts.
   */
  getOutputTableCountsQuery(
    connection: DataWarehouseConnection,
    componentName: string,
    params: Record<string, unknown>
  ) {
    return connection.getOutputTableCountsQuery(this, componentName, params)
  }
}

export { Workflow }
